import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    Stack,
    HStack,
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PDButton from '../../components/pd-button'
import usePdTagManager from '../../../../hooks/use-pd-tag-manager'

export const HeroPromotionUpToPercentOff = ({
    variant = 'hero',

    desktopPromotionBackground = 'transparent',
    mobilePromotionBackground = 'transparent',
    desktopPromotionPosition = 'middle-center',
    mobilePromotionPosition = 'middle',

    titleText,
    titleFontFamily = 'caslon',
    titleColor = 'black',
    titleTag = 'h2',

    subTitleText,
    subTitleFontFamily = 'caslon',
    subTitleColor = 'black',
    subTitleTag = 'h3',

    ctaTypeMobile = 'button',
    ctaTypeDesktop = 'button',
    buttonFontFamily = 'inter',

    buttonText1,
    buttonTextColor1,
    buttonBgColor1,
    buttonLink1,

    dropdownLinkLabel1,
    dropdownLinkUrl1,
    dropdownLinkLabel2,
    dropdownLinkUrl2,
    dropdownLinkLabel3,
    dropdownLinkUrl3,
    dropdownLinkLabel4,
    dropdownLinkUrl4,
    dropdownLinkLabel5,
    dropdownLinkUrl5,
    dropdownLinkLabel6,
    dropdownLinkUrl6,

    buttonText2,
    buttonLink2,
    buttonTextColor2,
    buttonBgColor2,

    typeId,
    name
}) => {
    const styles = useMultiStyleConfig('HeroPromotions', {
        variant: `${variant}UpToPercentOff`
    })
    const pdTagManager = usePdTagManager()
    useEffect(() => {
        pdTagManager.viewComponent(typeId, name)
    }, [])
    const hasButton1 = buttonText1 && buttonLink1
    const hasButton2 = buttonText2 && buttonLink2
    const ctaType1 = useBreakpointValue({base: ctaTypeMobile, lg: ctaTypeDesktop})
    const ctaType2 = useBreakpointValue({base: ctaTypeMobile, lg: ctaTypeDesktop})

    const dropdownLinks1 = [
        dropdownLinkLabel1 &&
            dropdownLinkUrl1 && {label: dropdownLinkLabel1, url: dropdownLinkUrl1},
        dropdownLinkLabel2 &&
            dropdownLinkUrl2 && {label: dropdownLinkLabel2, url: dropdownLinkUrl2},
        dropdownLinkLabel3 &&
            dropdownLinkUrl3 && {label: dropdownLinkLabel3, url: dropdownLinkUrl3},
        dropdownLinkLabel4 &&
            dropdownLinkUrl4 && {label: dropdownLinkLabel4, url: dropdownLinkUrl4},
        dropdownLinkLabel5 &&
            dropdownLinkUrl5 && {label: dropdownLinkLabel5, url: dropdownLinkUrl5},
        dropdownLinkLabel6 && dropdownLinkUrl6 && {label: dropdownLinkLabel6, url: dropdownLinkUrl6}
    ].filter(Boolean)

    return (
        <Stack
            gridArea={{base: mobilePromotionPosition, md: desktopPromotionPosition}}
            height="100%"
            justifyContent="center"
            position="relative"
        >
            <Stack
                backgroundColor={{base: mobilePromotionBackground, lg: desktopPromotionBackground}}
                {...styles.wrapper}
                className="promo-wrapper text-stack"
            >
                {titleText && (
                    <HStack justifyContent="center">
                        <Box color={titleColor} {...styles.titleInner} className="title-inner">
                            <Text>Up</Text>
                            <Text>To</Text>
                        </Box>

                        <Text
                            as={titleTag}
                            className="title-text"
                            fontFamily={titleFontFamily}
                            color={titleColor}
                            whiteSpace="nowrap"
                            {...styles.title}
                        >
                            {titleText}
                        </Text>
                    </HStack>
                )}

                {subTitleText && (
                    <Text
                        as={subTitleTag}
                        className="sub-title-text"
                        fontFamily={subTitleFontFamily}
                        color={subTitleColor}
                        {...styles.subTitle}
                    >
                        {subTitleText}
                    </Text>
                )}

                {hasButton1 || hasButton2 ? (
                    <HStack
                        gap={{base: '20px', md: '20px'}}
                        justifyContent="center"
                        flexWrap="wrap"
                        className="promo-buttons-wrapper"
                        marginTop={{base: '20px', lg: '25px'}}
                    >
                        {hasButton1 && (
                            <PDButton
                                text={buttonText1}
                                url={buttonLink1}
                                color={buttonTextColor1}
                                ctaType={ctaType1}
                                buttonBackgroundColor={buttonBgColor1}
                                buttonFontFamily={buttonFontFamily}
                                links={dropdownLinks1}
                                typeId={typeId}
                                name={name}
                            />
                        )}

                        {hasButton2 && (
                            <PDButton
                                text={buttonText2}
                                url={buttonLink2}
                                color={buttonTextColor2}
                                ctaType={ctaType2}
                                buttonBackgroundColor={buttonBgColor2}
                                buttonFontFamily={buttonFontFamily}
                                typeId={typeId}
                                name={name}
                            />
                        )}
                    </HStack>
                ) : null}
            </Stack>
        </Stack>
    )
}

HeroPromotionUpToPercentOff.propTypes = {
    variant: PropTypes.string,
    desktopPromotionBackground: PropTypes.string,
    mobilePromotionBackground: PropTypes.string,
    desktopPromotionPosition: PropTypes.string,
    mobilePromotionPosition: PropTypes.string,

    titleText: PropTypes.string,
    titleFontFamily: PropTypes.string,
    titleColor: PropTypes.string,
    titleTag: PropTypes.string,

    subTitleText: PropTypes.string,
    subTitleFontFamily: PropTypes.string,
    subTitleColor: PropTypes.string,
    subTitleTag: PropTypes.string,

    ctaTypeMobile: PropTypes.string,
    ctaTypeDesktop: PropTypes.string,
    ctaSizeMobile: PropTypes.string,
    ctaSizeDesktop: PropTypes.string,

    buttonText1: PropTypes.string,
    buttonColor1: PropTypes.string,
    buttonTextColor1: PropTypes.string,
    buttonBgColor1: PropTypes.string,
    buttonTextColor2: PropTypes.string,
    buttonBgColor2: PropTypes.string,
    buttonLink1: PropTypes.string,
    buttonFontFamily: PropTypes.string,
    dropdownLinkLabel1: PropTypes.string,
    dropdownLinkUrl1: PropTypes.string,
    dropdownLinkLabel2: PropTypes.string,
    dropdownLinkUrl2: PropTypes.string,
    dropdownLinkLabel3: PropTypes.string,
    dropdownLinkUrl3: PropTypes.string,
    dropdownLinkLabel4: PropTypes.string,
    dropdownLinkUrl4: PropTypes.string,
    dropdownLinkLabel5: PropTypes.string,
    dropdownLinkUrl5: PropTypes.string,
    dropdownLinkLabel6: PropTypes.string,
    dropdownLinkUrl6: PropTypes.string,

    buttonText2: PropTypes.string,
    buttonLink2: PropTypes.string,
    typeId: PropTypes.string,
    name: PropTypes.any
}

export default HeroPromotionUpToPercentOff
